import {
  Blend,
  Box,
  Camera,
  Clapperboard,
  Eye,
  Flame,
  Layers,
  SlidersHorizontal,
  WandSparkles,
  Waves,
  type LucideIcon,
} from "lucide-react";
import type { CSSProperties, DragEvent, Ref } from "react";

import type { TransitionKind } from "../../api/generated/model";
import {
  TRANSITIONS,
  TRANSITION_CATEGORIES,
  TRANSITION_DRAG_TYPE,
  searchTransitions,
  type TransitionCategoryId,
  type TransitionInfo,
} from "./transition-catalog";
import { TileCatalog } from "./TileCatalog";
import { TILE_PREVIEW_DELAY_MS, useTransitionPreview } from "./hover-previews";
import classes from "./transitions.module.css";

/** A card's look, as live draws a tile whose preview did not load: a wash, an icon, the name. */
interface CardLook {
  pattern: string;
  Icon: LucideIcon;
}

const LOOKS: Record<"light" | "camera" | "blur" | "3d" | "glitch" | "distort" | "shapes" | "classic" | "stylized", CardLook> = {
  light: {
    pattern: "radial-gradient(circle at center, rgba(255,255,255,0.8) 0%, rgba(245,158,11,0.4) 40%, rgba(220,38,38,0.2) 100%)", // raw-color: live's card
    Icon: Flame,
  },
  camera: {
    pattern: "radial-gradient(circle at center, rgba(59,130,246,0.5) 0%, rgba(30,58,138,0.3) 60%, transparent 100%)", // raw-color: live's card
    Icon: Camera,
  },
  blur: {
    pattern: "radial-gradient(circle at center, rgba(168,85,247,0.4) 0%, rgba(139,92,246,0.2) 70%, transparent 100%)", // raw-color: live's card
    Icon: Eye,
  },
  "3d": {
    pattern: "linear-gradient(135deg, rgba(14,165,233,0.4) 0%, rgba(30,64,175,0.3) 100%)", // raw-color: live's card
    Icon: Box,
  },
  glitch: {
    pattern: "repeating-linear-gradient(0deg, rgba(236,72,153,0.3) 0px, rgba(6,182,212,0.3) 4px, transparent 4px, transparent 8px)", // raw-color: live's card
    Icon: SlidersHorizontal,
  },
  distort: {
    pattern: "radial-gradient(circle at center, rgba(20,184,166,0.5) 0%, rgba(13,148,136,0.3) 50%, transparent 100%)", // raw-color: live's card
    Icon: Waves,
  },
  shapes: {
    pattern: "linear-gradient(45deg, rgba(139,92,246,0.4) 25%, transparent 25%, transparent 75%, rgba(139,92,246,0.4) 75%)", // raw-color: live's card
    Icon: Layers,
  },
  classic: {
    pattern: "radial-gradient(circle at center, rgba(99,102,241,0.3) 0%, transparent 70%)", // raw-color: live's card
    Icon: Clapperboard,
  },
  stylized: {
    pattern: "linear-gradient(135deg, rgba(217,70,239,0.4) 0%, rgba(245,158,11,0.3) 100%)", // raw-color: live's card
    Icon: WandSparkles,
  },
};

/**
 * Live's card for a tile: by its category, though a name that flashes, burns or exposes looks
 * like Light, and one that zooms, pans or spins like Camera, as live picks them.
 */
function lookOf({ category, name }: TransitionInfo): CardLook {
  const words = name.toLowerCase();
  if (category === "light" || /flash|burn|exposure/.test(words)) return LOOKS.light;
  if (category === "camera" || /zoom|pan|spin/.test(words)) return LOOKS.camera;
  const by: Record<Exclude<TransitionCategoryId, "light" | "camera">, CardLook> = {
    blur: LOOKS.blur,
    "3d": LOOKS["3d"],
    glitch: LOOKS.glitch,
    distort: LOOKS.distort,
    wipe: LOOKS.shapes,
    geometric: LOOKS.shapes,
    classic: LOOKS.classic,
    stylized: LOOKS.stylized,
  };
  return by[category];
}

export interface TransitionsTabProps {
  /** False when the member may not change the project: no tile applies. */
  canApply: boolean;
  /** Live's tile click: the kind on the cut nearest the playhead. */
  onApply: (kind: TransitionKind) => void;
  /** A tile's drag to the timeline starts (its kind) or ends (null). */
  onDrag: (kind: TransitionKind | null) => void;
}

/**
 * Live's Transitions tab (#240): "Search transitions...", then a row per category with "View
 * all", or one category's or the search's tiles in a grid. A tile is live's card (its preview
 * did not load on live either) over its name; a click puts it on the cut nearest the playhead, a
 * drag on the cut it is let go by.
 */
export function TransitionsTab(props: TransitionsTabProps) {
  return (
    <TileCatalog
      noun="transitions"
      categories={TRANSITION_CATEGORIES}
      items={TRANSITIONS}
      categoryOf={(info) => info.category}
      keyOf={(info) => info.key}
      search={searchTransitions}
      renderTile={(info) => <Tile info={info} {...props} />}
      NoneIcon={Blend}
    />
  );
}

/**
 * Live's card: its wash, an icon in a dark round, and the name in small white letters; over it,
 * `slot`, where its hover preview plays.
 */
export function TransitionCard({ info, slot }: { info: TransitionInfo; slot?: Ref<HTMLSpanElement> }) {
  const { pattern, Icon } = lookOf(info);
  return (
    <span className={classes.card} style={{ "--card-pattern": pattern } as CSSProperties} aria-hidden>
      <span className={classes.cardIcon}>
        <Icon size={14} />
      </span>
      <span className={classes.cardName}>{info.name}</span>
      <span ref={slot} className={classes.preview} />
    </span>
  );
}

function Tile({ info, canApply, onApply, onDrag }: TransitionsTabProps & { info: TransitionInfo }) {
  const { kind } = info;
  const { slot, hover } = useTransitionPreview(kind, TILE_PREVIEW_DELAY_MS);
  const face = (
    <>
      <TransitionCard info={info} slot={slot} />
      <span className={classes.tileName}>{info.name}</span>
    </>
  );
  const start = (event: DragEvent<HTMLButtonElement>) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(TRANSITION_DRAG_TYPE, kind);
    onDrag(kind);
  };
  return (
    <button
      type="button"
      className={classes.tile}
      aria-label={info.name}
      disabled={!canApply}
      draggable={canApply}
      onClick={() => onApply(kind)}
      onDragStart={start}
      onDragEnd={() => onDrag(null)}
      {...hover}
    >
      {face}
    </button>
  );
}
